"use client";

import { c as _c } from "react/compiler-runtime";
import { useSyncExternalStore } from "react";
import { motion, useReducedMotion } from "motion/react";
import { Button } from "@/components/ui/button";
import { IOS_APP_STORE_URL, buildAndroidPlayUrl, detectPlatformTarget, type PlatformTarget } from "@/lib/marketing/links";
import { postMarketingEvent, resolveMarketingUtm } from "@/lib/marketing/events-client";
import { cn } from "@/lib/utils";
type DownloadVariant = "hero" | "cta";

// Stable subscribe: platform/UTM are read-once from the browser and never
// change for the life of the page, so there is nothing to subscribe to.
const subscribeNoop = () => () => {};

/**
 * Reads the visitor's platform target client-side. Returns null on the server
 * (and during the first client render) so hydration matches, then resolves to
 * the real value once mounted. useSyncExternalStore is the React-recommended
 * way to read a browser-only value without triggering a cascading effect.
 */
function usePlatformTarget() {
  return useSyncExternalStore(subscribeNoop, _temp, _temp2);
}
function _temp2() {
  return null;
}
function _temp() {
  return detectPlatformTarget(navigator.userAgent);
}
function GooglePlayGlyph() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false"><path d="M3.6 2.3 13 11.7l2.6-2.6L5.2 1.9a1.6 1.6 0 0 0-1.6.4Z" fill="currentColor" /><path d="M2.8 2.9A1.6 1.6 0 0 0 2.4 4v16c0 .4.1.8.4 1.1L11.6 12 2.8 2.9Z" fill="currentColor" /><path d="M16.6 9.7 13.9 12l2.7 2.3 3.4-2a1.6 1.6 0 0 0 0-2.7l-3.4-1.9Z" fill="currentColor" /><path d="M13 12.3 3.6 21.7c.5.3 1.1.3 1.6 0l10.4-6 .1-.1L13 12.3Z" fill="currentColor" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function AppleGlyph() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false"><path d="M16.4 12.8c0-2.3 1.9-3.4 2-3.5-1.1-1.6-2.8-1.8-3.4-1.8-1.4-.1-2.8.9-3.5.9-.7 0-1.8-.8-3-.8-1.5 0-3 .9-3.8 2.3-1.6 2.8-.4 7 1.2 9.3.8 1.1 1.7 2.4 2.9 2.3 1.2-.1 1.6-.8 3-.8s1.8.8 3 .7c1.2 0 2-1.1 2.8-2.3.9-1.3 1.2-2.6 1.3-2.6-.1 0-2.4-.9-2.5-3.7Z" /><path d="M14.3 6c.6-.8 1-1.9.9-3-1 0-2.1.6-2.8 1.4-.6.7-1.1 1.8-1 2.9 1.1.1 2.2-.5 2.9-1.3Z" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}

/**
 * Anonymous download CTAs. Detects the visitor's platform client-side and
 * highlights the matching store button; both buttons stay usable so a visitor
 * on either platform can always pick the other.
 */
export function DownloadButtons(t0) {
  const $ = _c(34);
  const {
    variant: t1
  } = t0;
  const variant = t1 === undefined ? "hero" : t1;
  const target = usePlatformTarget();
  let t2;
  if ($[0] !== target) {
    t2 = target === null ? buildAndroidPlayUrl() : buildAndroidPlayUrl(resolveMarketingUtm());
    $[0] = target;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const androidHref = t2;
  const iosPrimary = target === "ios";
  const androidPrimary = target === "android";
  const handleClick = _temp3;
  const reduce = useReducedMotion();
  let t3;
  if ($[2] !== reduce) {
    t3 = reduce ? {} : {
      whileHover: {
        scale: 1.03,
        y: -2
      },
      whileTap: {
        scale: 0.97
      }
    };
    $[2] = reduce;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const hover = t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <span aria-hidden="true" className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/25 to-transparent transition-transform duration-700 ease-out group-hover:translate-x-full" />;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const shine = t4;
  const t5 = androidPrimary ? "default" : "outline";
  const t6 = androidPrimary ? "bg-red-600 text-white hover:bg-red-500 shadow-[0_0_2.5rem_-0.5rem_rgba(220,38,38,0.7)]" : "border-white/15 bg-white/5 text-white hover:bg-white/10";
  let t7;
  if ($[5] !== t6) {
    t7 = cn("group relative w-full overflow-hidden sm:w-auto", "h-12 px-6 text-base", t6);
    $[5] = t6;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => handleClick("android");
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  let t9;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <span className="flex size-5 items-center justify-center"><GooglePlayGlyph /></span>;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  let t10;
  if ($[9] !== androidHref) {
    t10 = <a href={androidHref} target="_blank" rel="noopener noreferrer" onClick={t8}>{shine}{t9}Get it on Google Play</a>;
    $[9] = androidHref;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  let t11;
  if ($[11] !== t10 || $[12] !== t5 || $[13] !== t7) {
    t11 = <Button asChild={true} size="lg" variant={t5} className={t7}>{t10}</Button>;
    $[11] = t10;
    $[12] = t5;
    $[13] = t7;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== hover || $[16] !== t11) {
    t12 = <motion.div {...hover} className="w-full sm:w-auto">{t11}</motion.div>;
    $[15] = hover;
    $[16] = t11;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  const t13 = iosPrimary ? "default" : "outline";
  const t14 = iosPrimary ? "bg-red-600 text-white hover:bg-red-500 shadow-[0_0_2.5rem_-0.5rem_rgba(220,38,38,0.7)]" : "border-white/15 bg-white/5 text-white hover:bg-white/10";
  let t15;
  if ($[18] !== t14) {
    t15 = cn("group relative w-full overflow-hidden sm:w-auto", "h-12 px-6 text-base", t14);
    $[18] = t14;
    $[19] = t15;
  } else {
    t15 = $[19];
  }
  let t16;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = () => handleClick("ios");
    $[20] = t16;
  } else {
    t16 = $[20];
  }
  let t17;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <a href={IOS_APP_STORE_URL} target="_blank" rel="noopener noreferrer" onClick={t16}>{shine}<span className="flex size-5 items-center justify-center"><AppleGlyph /></span>Download on the App Store</a>;
    $[21] = t17;
  } else {
    t17 = $[21];
  }
  let t18;
  if ($[22] !== t13 || $[23] !== t15) {
    t18 = <Button asChild={true} size="lg" variant={t13} className={t15}>{t17}</Button>;
    $[22] = t13;
    $[23] = t15;
    $[24] = t18;
  } else {
    t18 = $[24];
  }
  let t19;
  if ($[25] !== hover || $[26] !== t18) {
    t19 = <motion.div {...hover} className="w-full sm:w-auto">{t18}</motion.div>;
    $[25] = hover;
    $[26] = t18;
    $[27] = t19;
  } else {
    t19 = $[27];
  }
  let t20;
  if ($[28] !== t12 || $[29] !== t19) {
    t20 = <div className="flex w-full flex-col gap-3 sm:flex-row sm:flex-wrap sm:justify-center">{t12}{t19}</div>;
    $[28] = t12;
    $[29] = t19;
    $[30] = t20;
  } else {
    t20 = $[30];
  }
  let t21;
  if ($[31] !== t20 || $[32] !== variant) {
    t21 = <div className="w-full" data-download-variant={variant}>{t20}</div>;
    $[31] = t20;
    $[32] = variant;
    $[33] = t21;
  } else {
    t21 = $[33];
  }
  return t21;
}
function _temp3(platform) {
  postMarketingEvent("marketing_download_click", {
    platform_target: platform
  });
}